import { debounce } from "lodash";
import React, { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";

export const useAutocompleteSuggestions = (
  name: string,
  autoCompleteFormula: (input: string) => Promise<string[]>,
  debounceWait: number,
  maxWait: number
) => {
  const [suggestions, setSuggestions] = React.useState<string[]>([]);

  const { setValue, watch, clearErrors } = useFormContext();

  // Create a stable debounced function with useRef
  const value = watch(name);
  const debouncedFetchSuggestions = useRef(
    debounce(
      async (input: string) => {
        const newSuggestions = await autoCompleteFormula(input);
        // Edge case - input is equal to the only suggestion
        if (newSuggestions.length === 1 && newSuggestions[0] === input) {
          clearErrors(name);
          setSuggestions([]);
        } else {
          setSuggestions(newSuggestions);
        }
      },
      debounceWait,
      { maxWait }
    )
  ).current;

  useEffect(() => {
    if (value) {
      debouncedFetchSuggestions(value);
    } else {
      setSuggestions([]);
    }

    return () => debouncedFetchSuggestions.cancel();
  }, [value, debouncedFetchSuggestions]);

  const onSelectSuggestion = (suggestion: string) => {
    setValue(name, suggestion);
    setSuggestions([]);
    clearErrors(name);
  };

  return {
    suggestions,
    onSelectSuggestion
  };
};
